import { useEffect, useRef, useState } from 'react'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../constants/common'
import { MicrophoneStreamError } from '../../constants/microphoneStream'

import {
  DEFAULT_CHUNK_MS,
  DEFAULT_MIME_TYPE,
} from './useMicrophoneStream.constants'
import {
  isMediaRecorderInactive,
  isMediaRecorderSupported,
  isMicrophonePermissionError,
} from './useMicrophoneStream.utils'

export function useMicrophoneStream({
  chunkMs = DEFAULT_CHUNK_MS,
  mimeType = DEFAULT_MIME_TYPE,
  onChunk = EMPTY_FUNCTION,
  onError = EMPTY_FUNCTION,
  onReady = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const [isAcquired, setIsAcquired] = useState(false)

  const callbacksRef = useRef({ onChunk, onError, onReady })
  const isMountedRef = useRef(true)

  // Latest-ref sync (intentionally no deps). Consumer callbacks are typically
  // inline, so their identities change every render — listing them as deps
  // would re-run this effect every render anyway. Omitting the array makes the
  // "sync on every commit" intent explicit. MediaRecorder event handlers read
  // these through the ref so they always invoke the latest consumer callbacks
  // without forcing acquire/release to be recreated.
  useEffect(() => {
    const callbacks = callbacksRef.current

    callbacks.onChunk = onChunk
    callbacks.onError = onError
    callbacks.onReady = onReady
  })

  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  // Bumped on each acquire()/release(); async closures bail when the token shifts.
  const sessionTokenRef = useRef(0)

  function release() {
    sessionTokenRef.current += 1

    const recorder = recorderRef.current

    recorderRef.current = null

    if (recorder) {
      recorder.ondataavailable = null
      recorder.onerror = null
      recorder.onstop = null

      try {
        if (!isMediaRecorderInactive(recorder)) {
          recorder.stop()
        }
      } catch {
        // MediaRecorder.stop() throws if already stopped — safe to ignore.
      }
    }

    const stream = streamRef.current

    streamRef.current = null

    if (stream) {
      try {
        stream.getTracks().forEach(track => track.stop())
      } catch {
        // MediaStreamTrack.stop() may throw if the track already ended.
      }
    }

    if (isMountedRef.current) {
      setIsAcquired(false)
    }
  }

  function acquire() {
    if (!isMediaRecorderSupported(mimeType)) {
      callbacksRef.current.onError(MicrophoneStreamError.RecorderUnsupported)

      return
    }

    // Guard against environments where `navigator.mediaDevices.getUserMedia`
    // isn't present (older browsers, insecure contexts, Jest/node). Without
    // this check `getUserMedia(...)` would throw synchronously and bypass the
    // promise `.catch`, leaving the hook in an inconsistent state.
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      callbacksRef.current.onError(MicrophoneStreamError.Unavailable)

      return
    }

    const sessionToken = ++sessionTokenRef.current

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        if (sessionTokenRef.current === sessionToken) {
          streamRef.current = stream

          let recorder = null

          try {
            recorder = new MediaRecorder(stream, { mimeType })
          } catch {
            release()
            callbacksRef.current.onError(
              MicrophoneStreamError.RecorderUnsupported
            )
          }

          if (recorder) {
            recorderRef.current = recorder

            recorder.ondataavailable = event => {
              if (
                sessionTokenRef.current === sessionToken &&
                event.data &&
                event.data.size > 0
              ) {
                callbacksRef.current.onChunk(event.data)
              }
            }

            recorder.onerror = () => {
              if (sessionTokenRef.current === sessionToken) {
                callbacksRef.current.onError(
                  MicrophoneStreamError.RecorderError
                )
              }
            }

            if (isMountedRef.current) {
              setIsAcquired(true)
            }

            callbacksRef.current.onReady()
          }
        } else {
          stream.getTracks().forEach(track => track.stop())
        }
      })
      .catch(error => {
        if (sessionTokenRef.current === sessionToken) {
          if (isMicrophonePermissionError(error)) {
            callbacksRef.current.onError(MicrophoneStreamError.PermissionDenied)
          } else {
            callbacksRef.current.onError(MicrophoneStreamError.Unavailable)
          }
        }
      })
  }

  function startCapture() {
    const recorder = recorderRef.current

    if (recorder) {
      try {
        recorder.start(chunkMs)

        return true
      } catch {
        callbacksRef.current.onError(MicrophoneStreamError.RecorderError)
      }
    }

    return false
  }

  function stopCapture() {
    const recorder = recorderRef.current

    if (recorder && !isMediaRecorderInactive(recorder)) {
      try {
        recorder.stop()
      } catch {
        // MediaRecorder.stop() throws if not in a recording state.
      }
    }
  }

  const releaseRef = useRef(release)

  useEffect(() => {
    releaseRef.current = release
  }, [release])

  // Bind teardown to mount/unmount only — reading `release` through a ref
  // keeps this effect from re-firing if its dep graph changes.
  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      releaseRef.current()
    }
  }, [])

  return {
    acquire,
    isAcquired,
    release,
    startCapture,
    stopCapture,
  }
}
